import { Injectable, inject } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import {
  DnaProdigyGuideline,
  DnaProdigyGuidelineParentingApproachQueryResult,
  Query
} from '@interfaces/schema';

/**
 * Prodigy Health Guideline Service.
 *
 * @exports
 * @class ProdigyHealthGuidelineService
 */
@Injectable()
export class ProdigyHealthGuidelineService {
  private readonly apollo = inject(Apollo);

  /**
   * Fetch Prodigy Guideline Child Growth and Development .
   *
   * @returns {Observable<Pick<DnaProdigyGuideline, 'id' | 'proGuidelineCgadActivity'>>}
   * @memberof ProdigyHealthGuidelineService
   */
  fetchCgadActivity(): Observable<
    Pick<DnaProdigyGuideline, 'id' | 'proGuidelineCgadActivity'>
  > {
    const query = gql`
      query GetProdigyGuidlineByUser($include: [String]) {
        DNA {
          ProdigyGuideline {
            findByUser(include: $include) {
              id
              proGuidelineCgadActivity {
                id
                name
                type
                icon
                insight
                position
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: {
          include: ['rpx-prodigy-guideline-cgad-activity']
        }
      })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.ProdigyGuideline?.findByUser as Pick<
              DnaProdigyGuideline,
              'id' | 'proGuidelineCgadActivity'
            >
        )
      );
  }

  /**
   * Fetch Prodigy Guideline Child Insight.
   *
   * @returns {Observable<Pick<DnaProdigyGuideline, 'id' | 'proGuidelineChildInsight'>>}
   * @memberof ProdigyHealthGuidelineService
   */
  fetchChildInsight(): Observable<
    Pick<DnaProdigyGuideline, 'id' | 'proGuidelineChildInsight'>
  > {
    const query = gql`
      query GetProdigyGuidlineByUser($include: [String]) {
        DNA {
          ProdigyGuideline {
            findByUser(include: $include) {
              id
              proGuidelineChildInsight {
                id
                title
                insight
                category
                position
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: {
          include: ['rpx-prodigy-guideline-child-insight']
        }
      })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.ProdigyGuideline?.findByUser as Pick<
              DnaProdigyGuideline,
              'id' | 'proGuidelineChildInsight'
            >
        )
      );
  }

  /**
   * Fetch Prodigy Guideline Parenting Approach.
   *
   * @returns {Observable<DnaProdigyGuidelineParentingApproachQueryResult>}
   * @memberof ProdigyHealthGuidelineService
   */
  fetchParentingApproach(): Observable<DnaProdigyGuidelineParentingApproachQueryResult> {
    const query = gql`
      query GetProdigyGuidlineParentingByUser($include: [String]) {
        DNA {
          ProdigyGuideline {
            ParentingApproach {
              findByUser(include: $include) {
                count
                results {
                  id
                  icon
                  title
                  category
                  position
                  description
                  proGuidelineParentingApproachInsight {
                    id
                    title
                    insight
                    position
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.apollo
      .query<Query>({
        query,
        variables: {
          include: ['rpx-prodigy-guideline-parenting-approach-insight']
        }
      })
      .pipe(
        map(
          ({ data }) =>
            data?.DNA?.ProdigyGuideline?.ParentingApproach
              ?.findByUser as DnaProdigyGuidelineParentingApproachQueryResult
        )
      );
  }
}
